import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';

import Breadcrumbs from './Breadcrumbs';
import Header from './Header';
import ListViewItem from './ListViewItem';
import { IFigureDto } from '../models/IFigureDto';
import FigurService from '../services/figureService';
import { FavoritesContext } from '../context/FavoritesContext';
import { IFigureCollection } from '../models/IFigureCollection';
import { useConfig } from '../config/env';
import { useAppEvent } from '../hooks/useAppEvent';
import { applyFigureRemovedToInfiniteData, applyFigureUpdatedToInfiniteData } from '../query/figureCacheUpdates';

// Define query data type for better type safety
type QueryData = {
  pages: Array<{
    data: IFigureDto[];
    hasMore: boolean;
    nextCursor: string | null;
    totalCount: number;
  }>;
  pageParams: number[];
};

type ViewType = 'list' | 'grid';

interface ListViewProps {
  collectionId: number;
  view: ViewType;
  onViewChange: (event: React.MouseEvent<HTMLElement>, newView: ViewType | null) => void;
  onFigureSelect: (figure: IFigureDto) => void;
  onForceGridView?: () => void;
  sharedSearchState?: {
    results: IFigureDto[];
    isSearchMode: boolean;
    totalCount: number;
    collectionId: number | null;
  } | null;
  onAvatarClick: (collectionId: number) => void;
  seriesId?: number | null;
  selectedTags?: string[];
  collections?: Array<{ id: number; name: string; color?: string }>;
}

const ListView: React.FC<ListViewProps> = ({
  collectionId,
  view,
  onViewChange,
  onFigureSelect,
  onForceGridView,
  sharedSearchState,
  onAvatarClick,
  seriesId,
  selectedTags = [],
  collections = [],
}) => {
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const { favorites, refreshFavorites } = useContext(FavoritesContext);
  const [breadcrumbItems, setBreadcrumbItems] = useState<{ label: string; href?: string; onClick?: () => void }[]>([]);
  const [currentCollection, setCurrentCollection] = useState<IFigureCollection>();
  const limit = 100; // Number of items to fetch per page
  const [totalLoaded, setTotalLoaded] = useState<number>(0);
  const [totalAvailable, setTotalAvailable] = useState<number>(0);
  const ITEM_HEIGHT = 80; // Approximate height of each list item in pixels
  const queryClient = useQueryClient();
  const [originalCollection, setOriginalCollection] = useState<IFigureCollection | undefined>();

  // Use shared search state for this collection
  const effectiveSearchResults = sharedSearchState?.collectionId === collectionId ? sharedSearchState.results : [];
  const effectiveIsSearchMode = sharedSearchState?.collectionId === collectionId ? sharedSearchState.isSearchMode : false;
  const effectiveSearchTotalCount = sharedSearchState?.collectionId === collectionId ? sharedSearchState.totalCount : 0;

  // Existing collection query
  const collectionQuery = useInfiniteQuery({
    queryKey: ['figures', collectionId, seriesId ?? 0],
    queryFn: async ({ pageParam = 1 }) => {
      console.log(`Fetching page ${pageParam} with limit ${limit}`);
      const result =
        collectionId === Number(cfg?.favoritterCollectionId ?? 0) // TODO settings for favorite collection should come from env/appsettings
          ? await FigurService.getFavoriteFigures(pageParam, limit, seriesId ?? 0)
          : await FigurService.getFigures(collectionId, pageParam, limit, seriesId ?? 0);

      return result;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      console.log(`hasMore: ${lastPage.hasMore}, nextCursor: ${lastPage.nextCursor}`);
      return lastPage.hasMore && lastPage.nextCursor ? parseInt(lastPage.nextCursor) : undefined;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: !effectiveIsSearchMode, // Disable collection query when searching
  });

  const { data, fetchNextPage, hasNextPage, isFetching, isLoading, isFetchingNextPage } = collectionQuery;

  // Helper function to check if a figure has any of the selected tags
  const figureHasTags = useCallback((figure: IFigureDto, tags: string[]): boolean => {
    if (tags.length === 0) return true;

    // Extract all tags from the figure (BFS and customer tags)
    const bfsTags: string[] = (figure.metadata || '')
      .split('\n')
      .filter(Boolean)
      .map((tag) => tag.trim().toLowerCase());

    // Extract customer tags (format: #tag:collectionId or #tag)
    const customerTags: string[] = (figure.customerMetadata || '')
      .split('#')
      .filter(Boolean)
      .map((tagStr) => {
        const parts = tagStr.trim().split(':');
        return parts[0].trim().toLowerCase();
      });

    const allFigureTags = [...bfsTags, ...customerTags];

    // Check if any selected tag matches any figure tag (case-insensitive)
    return tags.some((selectedTag) => allFigureTags.includes(selectedTag.toLowerCase()));
  }, []);

  // Calculate total figures loaded and available, with tag filtering
  const allFigures = effectiveIsSearchMode ? effectiveSearchResults : data?.pages.flatMap((page) => page.data) || [];
  const figures = useMemo(() => {
    if (selectedTags.length === 0) return allFigures;
    return allFigures.filter((figure) => figureHasTags(figure, selectedTags));
  }, [allFigures, selectedTags, figureHasTags]);

  useEffect(() => {
    if (figures.length > 0) {
      setTotalLoaded(figures.length);

      if (data?.pages[0].totalCount) {
        setTotalAvailable(data.pages[0].totalCount);
      }

      console.log(`Loaded ${figures.length} figures out of ${data?.pages[0].totalCount || 'unknown'} total`);
    }
  }, [figures.length, data?.pages]);

  // Load more data when approaching the end of the list
  const loadMoreItems = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      console.log('Loading more items...');
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  // Handle scrolling events in the virtualized list
  const handleListScroll = useCallback(
    ({ visibleStopIndex }: { visibleStartIndex: number; visibleStopIndex: number }) => {
      // If we're within 50 items of the end of our loaded data, fetch more
      if (figures.length - visibleStopIndex < 50 && hasNextPage && !isFetchingNextPage) {
        loadMoreItems();
      }
    },
    [figures.length, hasNextPage, isFetchingNextPage, loadMoreItems],
  );

  useAppEvent('figureRemoved', ({ removedFigureId, collectionId: removedFrom }) => {
    // eslint-disable-next-line no-console
    console.log(`ListView: Figure removed: ${removedFigureId} from ${removedFrom}`);

    // Update this list view's cache: drop avatar membership in-place.
    // If removal is from the current collection, also remove the figure row entirely.
    queryClient.setQueryData<QueryData>(['figures', collectionId, seriesId ?? 0], (oldData) => {
      return applyFigureRemovedToInfiniteData(oldData, {
        removedFigureId,
        removedFromCollectionId: removedFrom,
        currentCollectionId: collectionId,
      });
    });

    // Favorites special-case: ensure row is removed there as well when applicable
    if (collectionId === Number(cfg?.favoritterCollectionId ?? 0)) {
      queryClient.setQueryData<QueryData>(['figures', 0], (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            data: page.data.filter((f) => f.imageId !== removedFigureId),
            totalCount: Math.max(0, (page.totalCount || 0) - 1),
          })),
        };
      });
    }
  });

  // Handle figure updates (especially favorite status changes)
  useAppEvent('figureUpdated', (updatedFigure) => {
    // eslint-disable-next-line no-console
    console.log(`ListView: Received figureUpdated for figure ${updatedFigure.imageId}, customerFavorite=${updatedFigure.customerFavorite}`);

    // Update the figure in the queryClient cache for this specific collection
    queryClient.setQueryData<QueryData>(['figures', collectionId, seriesId ?? 0], (oldData) => {
      return applyFigureUpdatedToInfiniteData(oldData, updatedFigure);
    });

    // eslint-disable-next-line no-console
    console.log(`ListView: Updated figure ${updatedFigure.imageId} in collection ${collectionId}`);
  });

  // Refresh favorites when collection changes or on component mount
  useEffect(() => {
    refreshFavorites();
  }, [collectionId]);

  // Update when favorites change
  useEffect(() => {
    console.log(`ListView: Favorites changed in collection ${collectionId}`);
  }, [favorites, collectionId]);

  // Render each row of the list
  const Row = useCallback(
    ({ index, style }: ListChildComponentProps) => {
      if (index >= figures.length) {
        // Render a loading indicator at the end
        return (
          <Box style={style} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        );
      }

      const figure = figures[index];
      return (
        <Box style={style}>
          <ListViewItem
            key={`${figure.imageId}-${collectionId}-${index}`}
            figure={figure}
            collectionId={collectionId}
            onFigureSelect={onFigureSelect}
            onAvatarClick={onAvatarClick}
            isDefaultCollection={currentCollection?.isDefault ?? false}
            collections={collections}
          />
        </Box>
      );
    },
    [figures, collectionId, onFigureSelect, onAvatarClick, currentCollection, collections],
  );

  useEffect(() => {
    // Get initial container width
    if (containerRef.current && !containerWidth) {
      setContainerWidth(containerRef.current.getBoundingClientRect().width);
    }

    // Add resize listener
    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.getBoundingClientRect().width);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [containerWidth]);

  useEffect(() => {
    const updateBreadcrumbs = async () => {
      try {
        if (collectionId === Number(cfg?.favoritterCollectionId ?? 0)) {
          setBreadcrumbItems([{ label: 'Favoritter' }]);
        } else {
          const collection = await FigurService.getCollectionsByCustomer(1);
          const foundCollection = collection.find((c) => c.id === collectionId);
          setCurrentCollection(foundCollection);
          if (foundCollection) {
            const defaultId = Number(cfg?.defaultCollectionId ?? 0);
            setBreadcrumbItems([
              { label: 'Byggforskserien', onClick: defaultId > 0 ? () => onAvatarClick(defaultId) : undefined },
              { label: foundCollection.name },
            ]);
          }
        }
      } catch (error) {
        console.error('Error updating breadcrumbs:', error);
      }
    };

    if (!effectiveIsSearchMode) {
      updateBreadcrumbs();
    }
  }, [collectionId, effectiveIsSearchMode]);

  // Refresh favorites when collection changes or on component mount
  useEffect(() => {
    refreshFavorites();
  }, [collectionId, refreshFavorites]);

  // Clear search handler
  const handleClearSearch = useCallback(() => {
    setOriginalCollection(undefined);
  }, []);

  const cfg = useConfig();

  // Search breadcrumbs - include clickable Byggforskserien to navigate to default collection
  const searchBreadcrumbs = effectiveIsSearchMode
    ? [
        {
          label: 'Byggforskserien',
          onClick: Number(cfg?.defaultCollectionId ?? 0) > 0 ? () => onAvatarClick(Number(cfg?.defaultCollectionId ?? 0)) : undefined,
        },
        { label: 'Søkeresultat' },
      ]
    : breadcrumbItems;

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        minHeight: 0,
        opacity: isLoading ? 0 : 1,
        transition: 'opacity 0.2s ease-in-out',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}>
      <Box
        ref={containerRef}
        component="main"
        sx={{
          width: '100%',
          maxWidth: '1400px',
          margin: '0 auto',
          boxSizing: 'border-box',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}>
        <Box sx={{ pb: 0, pt: 1, px: 0 }}>
          <Breadcrumbs items={searchBreadcrumbs} />
        </Box>

        <Box sx={{ p: 0 }}>
          <Header
            title={
              effectiveIsSearchMode
                ? 'Søkeresultat'
                : collectionId === Number(cfg?.favoritterCollectionId ?? 0)
                ? 'Favoritter'
                : currentCollection?.name ?? 'Listevisning'
            }
            description={effectiveIsSearchMode ? `${effectiveSearchTotalCount} resultater` : currentCollection?.headerComment ?? ''}
            view={view}
            onViewChange={onViewChange}
            disableEditing={effectiveIsSearchMode}
            onDescriptionChange={
              effectiveIsSearchMode
                ? async () => {} // Disable editing in search mode
                : async (newDesc) => {
                    if (!currentCollection) return;
                    try {
                      const updatedCollection = await FigurService.updateCollectionDetails(
                        currentCollection.id,
                        currentCollection.name,
                        1, // Hardcoded customerId - needs proper value
                        'random comment',
                        newDesc,
                      );
                      setCurrentCollection(updatedCollection);
                    } catch (error) {
                      console.error('Error updating collection header:', error);
                    }
                  }
            }
          />

          {/* Display loading status */}
          {figures.length > 0 && (
            <Box sx={{ mt: 1, fontSize: '0.875rem', color: 'text.secondary' }}>
              {effectiveIsSearchMode
                ? `Viser ${effectiveSearchResults.length} av ${effectiveSearchTotalCount} søkeresultater`
                : `Viser ${totalLoaded} av ${totalAvailable} figurer`}
            </Box>
          )}

          <Box
            ref={listRef}
            sx={{
              mt: 2,
              position: 'relative',
              flex: 1,
              height: 'calc(100vh - 320px)', // Adjusted for search back button
              minHeight: 0,
            }}>
            {isLoading ? (
              // Skeleton loader that maintains width
              <Box sx={{ width: '100%' }}>
                {[1, 2, 3].map((n) => (
                  <Paper
                    key={n}
                    elevation={0}
                    variant="outlined"
                    sx={{
                      p: 2,
                      mb: 1,
                      display: 'flex',
                      gap: 2,
                      alignItems: 'center',
                      bgcolor: 'background.paper',
                    }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: 'grey.100',
                        borderRadius: 1,
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          height: 20,
                          width: '60%',
                          bgcolor: 'grey.100',
                          borderRadius: 0.5,
                          mb: 1,
                        }}
                      />
                      <Box
                        sx={{
                          height: 16,
                          width: '40%',
                          bgcolor: 'grey.100',
                          borderRadius: 0.5,
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        '& > div': {
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: 'grey.100',
                        },
                      }}>
                      <Box />
                      <Box />
                    </Box>
                  </Paper>
                ))}
              </Box>
            ) : (
              <Box sx={{ height: '100%' }}>
                {/* AutoSizer makes the list responsive to container size */}
                <AutoSizer>
                  {({ height, width }: { height: number; width: number }) => (
                    <FixedSizeList
                      height={height}
                      width={width}
                      itemCount={hasNextPage ? figures.length + 1 : figures.length} // Add one for loading indicator
                      itemSize={ITEM_HEIGHT}
                      onItemsRendered={({ visibleStartIndex, visibleStopIndex }) => handleListScroll({ visibleStartIndex, visibleStopIndex })}
                      overscanCount={20} // Render 20 items above and below visible area for smoother scrolling
                    >
                      {Row}
                    </FixedSizeList>
                  )}
                </AutoSizer>
              </Box>
            )}

            {/* Show "No figures found" message when appropriate */}
            {!isLoading && !isFetching && figures.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                {effectiveIsSearchMode ? 'Ingen søkeresultater funnet' : 'Ingen figurer funnet'}
              </Box>
            )}

            {isLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  width: containerWidth ? `${containerWidth}px` : 'auto', // FIX: Complete the ternary
                }}>
                <CircularProgress />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ListView;
